/**
 * Resolve a source's storage_url into something an embed element can load.
 *
 * SAS/direct URLs are self-authenticating and pass through untouched.
 * Proxy URLs (`storage_url_type === 'proxy'`, emitted for locally stored
 * source files) are relative paths into the RAG server's auth-gated
 * `/api/sources/{id}/download` endpoint. iframe/img elements cannot attach
 * the `x-api-key` header, so the file is fetched here with auth and handed
 * to the element as a blob: URL instead.
 */

import { useEffect, useState } from 'react'
import { useRagProxyBase } from '@/hooks/useRagProxyBase'
import { normalizeRagUrl } from '@/utils/ragUrl'
import type { DisplaySource } from '@/types/source'

export interface ProxiedStorageUrl {
  /** URL safe to hand to an iframe/img src, or null when unavailable */
  url: string | null
  /** 'loading' only while a proxy fetch is in flight */
  status: 'ready' | 'loading' | 'error'
}

export interface UseProxiedStorageUrlOptions {
  /** Per-persona RAG base override; falls back to the deployment base */
  ragServerUrl?: string
  /** RAG API key — same credential the chat connector sends as x-api-key */
  ragApiKey?: string
  /**
   * Force the blob's MIME type. Servers that predate the 2026-07-29 MIME
   * fix stream PDFs with an unusable Content-Type (e.g. "document"),
   * which embed elements refuse to render inline.
   */
  mimeType?: string
  /** Skip fetching when the caller won't embed the file (default true) */
  enabled?: boolean
}

export function useProxiedStorageUrl(
  source: DisplaySource,
  options: UseProxiedStorageUrlOptions = {}
): ProxiedStorageUrl {
  const { ragServerUrl, ragApiKey, mimeType, enabled = true } = options
  const globalRagUrl = useRagProxyBase()
  const isProxy =
    enabled && source.storage_url_type === 'proxy' && !!source.storage_url
  const [state, setState] = useState<ProxiedStorageUrl>({
    url: null,
    status: 'loading',
  })

  useEffect(() => {
    if (!isProxy) return
    let cancelled = false
    let objectUrl: string | null = null
    const controller = new AbortController()
    setState({ url: null, status: 'loading' })

    // Same-environment absolute URLs get rewritten to a same-origin
    // /rag-proxy path (matches RagConnector/sourceService) — a raw
    // absolute base would turn the x-api-key header into a CORS preflight.
    const base = normalizeRagUrl(ragServerUrl || globalRagUrl || '/rag-proxy').replace(
      /\/$/,
      ''
    )
    fetch(`${base}${source.storage_url}`, {
      headers: ragApiKey ? { 'x-api-key': ragApiKey } : undefined,
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Source fetch failed: ${response.status}`)
        }
        let blob = await response.blob()
        if (mimeType && blob.type !== mimeType) {
          blob = new Blob([blob], { type: mimeType })
        }
        objectUrl = URL.createObjectURL(blob)
        if (cancelled) {
          URL.revokeObjectURL(objectUrl)
        } else {
          setState({ url: objectUrl, status: 'ready' })
        }
      })
      .catch(() => {
        if (!cancelled) setState({ url: null, status: 'error' })
      })

    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [isProxy, source.storage_url, ragServerUrl, globalRagUrl, ragApiKey, mimeType])

  if (!isProxy) {
    // 'reference' URLs are opaque provenance strings (azure://…), not
    // something an embed element can load — report no usable URL so
    // callers fall back to the text passage.
    if (source.storage_url_type === 'reference') {
      return { url: null, status: 'ready' }
    }
    return { url: source.storage_url || null, status: 'ready' }
  }
  return state
}
